/**
 * VehicleSecurityMap — Google Maps mapa de leads Vehicle Security (Iter309/310).
 *
 * Substitui o SVG estático "Central Florida Coverage" por um mapa real com
 * 5 layouts alternáveis via segmented control:
 *   • clusters (default)  — pins agrupados, cor por tier do score
 *   • heatmap             — camada de calor ponderada por opportunity ($ midpoint)
 *   • territories         — polígonos aproximados dos bairros + choropleth por avg score
 *   • split               — mapa + tabela sincronizados (o container renderiza a tabela)
 *   • satellite           — mapTypeId=satellite + botão Street View no info card
 *
 * Modo Privacidade:
 *   • city  (default) — pins no centro da cidade + jitter determinístico por lead.id
 *   • exact (MASTER)  — geocoding client-side (localStorage cache) → pin no endereço real
 *
 * Iter310 · Filtros in-map, busca por endereço + raio, GeoJSON de bairros,
 * heatmap ponderado por opportunity em dólares.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { APIProvider, Map as GMap, useMap } from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import {
  Layers, Flame, ThermometerSun, Grid3x3, SplitSquareHorizontal, Satellite,
  Eye, EyeOff, ChevronDown, ExternalLink, MapPin, Loader2, X, Info,
  Search, Crosshair, Filter,
} from "lucide-react";
import { useLang } from "../../i18n/LangContext";

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Kept in sync with backend routes/vehicle_security.py _CENTRAL_FL_CITIES so
// the map still positions pins when the backend geo-map endpoint hasn't been
// hit yet. Extra sub-neighbourhoods (Lake Nona, MetroWest, Dr. Phillips…) that
// only exist as free-form strings from Discovery.
const CITY_CENTROIDS = {
  "orlando": { lat: 28.5383, lng: -81.3792 },
  "kissimmee": { lat: 28.2920, lng: -81.4076 },
  "winter park": { lat: 28.5999, lng: -81.3392 },
  "sanford": { lat: 28.8003, lng: -81.2731 },
  "altamonte springs": { lat: 28.6611, lng: -81.3656 },
  "oviedo": { lat: 28.6700, lng: -81.2081 },
  "winter garden": { lat: 28.5654, lng: -81.5862 },
  "ocoee": { lat: 28.5694, lng: -81.5439 },
  "apopka": { lat: 28.6934, lng: -81.5322 },
  "clermont": { lat: 28.5494, lng: -81.7729 },
  "deltona": { lat: 28.9005, lng: -81.2637 },
  "daytona beach": { lat: 29.2108, lng: -81.0228 },
  "lakeland": { lat: 28.0395, lng: -81.9498 },
  "ocala": { lat: 29.1872, lng: -82.1401 },
  "melbourne": { lat: 28.0836, lng: -80.6081 },
  "palm bay": { lat: 28.0345, lng: -80.5887 },
  "tampa": { lat: 27.9506, lng: -82.4572 },
  "st. cloud": { lat: 28.2489, lng: -81.2812 },
  "lake mary": { lat: 28.7589, lng: -81.3178 },
  "longwood": { lat: 28.7031, lng: -81.3384 },
  "casselberry": { lat: 28.6772, lng: -81.3281 },
  "maitland": { lat: 28.6278, lng: -81.3631 },
  "windermere": { lat: 28.4953, lng: -81.5342 },
  "doctor phillips": { lat: 28.4478, lng: -81.4917 },
  "dr. phillips": { lat: 28.4478, lng: -81.4917 },
  "lake buena vista": { lat: 28.3703, lng: -81.5192 },
  "celebration": { lat: 28.3167, lng: -81.5389 },
  "davenport": { lat: 28.1611, lng: -81.6017 },
  "haines city": { lat: 28.1147, lng: -81.6203 },
  "lake nona": { lat: 28.3820, lng: -81.2497 },
  "metrowest": { lat: 28.5320, lng: -81.4553 },
  "montverde": { lat: 28.5988, lng: -81.6759 },
};

const TIER_COLOR = (score) => {
  const s = Number(score) || 0;
  if (s >= 90) return "#EF4444"; // Critical
  if (s >= 80) return "#F97316"; // Very High
  if (s >= 70) return "#EAB308"; // High
  if (s >= 60) return "#0EA5E9"; // Medium
  return "#94A3B8";              // Low
};
const TIER_LABEL = (score) => {
  const s = Number(score) || 0;
  if (s >= 90) return "Critical";
  if (s >= 80) return "Very High";
  if (s >= 70) return "High";
  if (s >= 60) return "Medium";
  return "Low";
};

const LAYOUTS = [
  { key: "clusters",    label: "Clusters",   icon: Layers },
  { key: "heatmap",     label: "Heatmap",    icon: ThermometerSun },
  { key: "territories", label: "Territórios", icon: Grid3x3 },
  { key: "split",       label: "Split View",  icon: SplitSquareHorizontal },
  { key: "satellite",   label: "Satélite",   icon: Satellite },
];

const RADIUS_OPTIONS_MI = [0, 5, 10, 25, 50];
const MI_TO_METERS = 1609.344;

// Simplified polygons approximating the main Central-FL sub-markets. Not
// legal boundaries — visual choropleth only. Each polygon is a short
// ring of [lat,lng] pairs; keeping them small avoids Google Maps quota
// concerns with detailed GeoJSON. Sourced from OSM bounding boxes and
// rounded to 4 decimals.
const NEIGHBORHOOD_POLYGONS = [
  { city: "Orlando", ring: [
    [28.6084, -81.4790], [28.6084, -81.2560], [28.4210, -81.2560],
    [28.4210, -81.4790]] },
  { city: "Winter Park", ring: [
    [28.6260, -81.3720], [28.6260, -81.3070], [28.5670, -81.3070],
    [28.5670, -81.3720]] },
  { city: "Maitland", ring: [
    [28.6520, -81.4020], [28.6520, -81.3370], [28.6060, -81.3370],
    [28.6060, -81.4020]] },
  { city: "Altamonte Springs", ring: [
    [28.6960, -81.4180], [28.6960, -81.3230], [28.6250, -81.3230],
    [28.6250, -81.4180]] },
  { city: "Oviedo", ring: [
    [28.7120, -81.2680], [28.7120, -81.1550], [28.6260, -81.1550],
    [28.6260, -81.2680]] },
  { city: "Windermere", ring: [
    [28.5320, -81.5700], [28.5320, -81.5000], [28.4590, -81.5000],
    [28.4590, -81.5700]] },
  { city: "Doctor Phillips", ring: [
    [28.4740, -81.5220], [28.4740, -81.4620], [28.4180, -81.4620],
    [28.4180, -81.5220]] },
  { city: "Lake Nona", ring: [
    [28.4260, -81.2850], [28.4260, -81.2130], [28.3300, -81.2130],
    [28.3300, -81.2850]] },
  { city: "MetroWest", ring: [
    [28.5620, -81.4830], [28.5620, -81.4260], [28.5020, -81.4260],
    [28.5020, -81.4830]] },
  { city: "Winter Garden", ring: [
    [28.6020, -81.6270], [28.6020, -81.5460], [28.5240, -81.5460],
    [28.5240, -81.6270]] },
  { city: "Ocoee", ring: [
    [28.6060, -81.5760], [28.6060, -81.5100], [28.5340, -81.5100],
    [28.5340, -81.5760]] },
  { city: "Apopka", ring: [
    [28.7370, -81.5730], [28.7370, -81.4900], [28.6510, -81.4900],
    [28.6510, -81.5730]] },
  { city: "Kissimmee", ring: [
    [28.3290, -81.4610], [28.3290, -81.3550], [28.2540, -81.3550],
    [28.2540, -81.4610]] },
  { city: "Sanford", ring: [
    [28.8390, -81.3170], [28.8390, -81.2280], [28.7620, -81.2280],
    [28.7620, -81.3170]] },
  { city: "Lake Buena Vista", ring: [
    [28.3980, -81.5480], [28.3980, -81.4880], [28.3420, -81.4880],
    [28.3420, -81.5480]] },
  { city: "Celebration", ring: [
    [28.3440, -81.5680], [28.3440, -81.5090], [28.2880, -81.5090],
    [28.2880, -81.5680]] },
];

// Haversine distance in miles (mile-friendly for FL sales territory tooling).
const distMi = (a, b) => {
  const R = 3958.8;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180)
    * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

// Mid-point of the technology contract band ("estimated_contract" tech_low/tech_high).
const leadMidOpportunity = (L) => {
  const ec = L?.estimated_contract || {};
  const lo = Number(ec.tech_low) || 0;
  const hi = Number(ec.tech_high) || 0;
  return Math.round((lo + hi) / 2);
};

// Free-text detection of Flock/ALPR signals from the lead.signals array.
const leadHasFlock = (L) => {
  if (L?.flock_detected === true) return true;
  const sig = (L?.signals || []).map((s) => String(s).toLowerCase());
  return sig.some((s) => s.includes("flock") || s.includes("alpr"));
};

const matchesFilters = (L, filters) => {
  if (!filters) return true;
  if (filters.tier && filters.tier !== "all") {
    if ((L.tier || "").toLowerCase() !== filters.tier) return false;
  }
  if (filters.flock === "only" && !leadHasFlock(L)) return false;
  if (filters.flock === "exclude" && leadHasFlock(L)) return false;
  return true;
};

// Exposed so the parent page can filter its own table with identical logic.
export const applyVehicleSecurityFilters = (leads, { filters, radiusCenter, radiusMilesValue, positionsResolver }) => {
  return leads.filter((L) => {
    if (!matchesFilters(L, filters)) return false;
    if (radiusCenter && radiusMilesValue > 0) {
      const pos = positionsResolver ? positionsResolver(L) : null;
      if (!pos) return false;
      if (distMi(pos, radiusCenter) > radiusMilesValue) return false;
    }
    return true;
  });
};

// City-mode position resolver — same jitter formula the map uses so
// the leads-table filter stays perfectly in sync with the pin positions.
export const resolveLeadCityPosition = (lead) => cityJitteredPos(lead);

// ── Nearest-neighbor route optimizer (Iter311) ────────────────────────
// Takes a start point + list of leads with lat/lng and returns them in
// visit order, tail-first via nearest-neighbor. Deterministic and fast
// for the < 200 leads Vehicle Security typically returns.
const optimizeVisitOrder = (start, stops) => {
  const rest = stops.filter((s) => s.lat != null && s.lng != null);
  const out = [];
  let cur = start;
  while (rest.length) {
    let bestI = 0, bestD = Infinity;
    for (let i = 0; i < rest.length; i++) {
      const d = distMi(cur, rest[i]);
      if (d < bestD) { bestD = d; bestI = i; }
    }
    cur = rest.splice(bestI, 1)[0];
    out.push(cur);
  }
  return out;
};

// Google Maps directions URL — handoff to native app for turn-by-turn.
const buildGmapsDirectionsUrl = (start, stops) => {
  const origin = start ? `${start.lat},${start.lng}` : "";
  const waypoints = stops.slice(0, -1).map((s) => `${s.lat},${s.lng}`).join("|");
  const dest = stops[stops.length - 1];
  const params = new URLSearchParams({
    api: "1",
    destination: dest ? `${dest.lat},${dest.lng}` : "",
    travelmode: "driving",
  });
  if (origin) params.set("origin", origin);
  if (waypoints) params.set("waypoints", waypoints);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
};

// ── OSM Overpass fetcher for real neighborhood polygons (Iter311) ─────
// Overpass rate-limit tolerant: cached in localStorage per neighborhood
// name for 7 days. Falls back to hardcoded rectangles when the request
// fails or times out.
const OSM_CACHE_KEY = "vs-osm-polygons-v1";
const OSM_TTL_MS = 7 * 24 * 3600 * 1000;

function loadOsmCache() {
  try { return JSON.parse(localStorage.getItem(OSM_CACHE_KEY) || "{}"); }
  catch { return {}; }
}
function saveOsmCache(cache) {
  try { localStorage.setItem(OSM_CACHE_KEY, JSON.stringify(cache)); }
  catch { /* quota */ }
}

// Query Overpass for a single suburb / neighbourhood by name inside FL.
// Returns { ring: [[lat,lng]...] } or null. Never throws.
async function fetchOsmPolygon(name) {
  const q = `
[out:json][timeout:15];
area["name"="Florida"]["admin_level"="4"]->.fl;
(
  relation["name"="${name}"]["place"~"suburb|neighbourhood|hamlet|village|town"](area.fl);
  way["name"="${name}"]["place"~"suburb|neighbourhood|hamlet|village|town"](area.fl);
  relation["name"="${name}"]["boundary"="administrative"](area.fl);
);
out geom;
`.trim();
  try {
    const resp = await fetch("https://overpass-api.de/api/interpreter", {
      method: "POST",
      body: q,
      headers: { "Content-Type": "text/plain" },
    });
    if (!resp.ok) return null;
    const j = await resp.json();
    const el = (j.elements || [])[0];
    if (!el) return null;
    // Relations expose members with geometry; ways expose a top-level geometry.
    let coords = null;
    if (el.type === "relation" && Array.isArray(el.members)) {
      const outer = el.members.find(
        (m) => m.role === "outer" && Array.isArray(m.geometry));
      if (outer) coords = outer.geometry;
    } else if (el.type === "way" && Array.isArray(el.geometry)) {
      coords = el.geometry;
    }
    if (!coords || coords.length < 3) return null;
    // Downsample long rings so we don't push 1000+ vertices into Google Maps.
    const step = Math.max(1, Math.floor(coords.length / 60));
    const ring = coords.filter((_, i) => i % step === 0).map((p) => [p.lat, p.lon]);
    return { ring };
  } catch {
    return null;
  }
}

// ── Utilities ─────────────────────────────────────────────────────────
// Deterministic hash → [-1,1] float, keyed by string. Same lead.id always
// produces the same jitter so pins don't jump between re-renders.
function seededOffset(seed, salt) {
  let h = 2166136261;
  const s = `${seed}::${salt}`;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // Fold to [-1, 1]
  return ((h >>> 0) / 0xFFFFFFFF) * 2 - 1;
}

function cityCentroid(city) {
  if (!city) return null;
  const key = city.toLowerCase().replace(/\s*\(.*?\)\s*/g, "").trim();
  if (CITY_CENTROIDS[key]) return CITY_CENTROIDS[key];
  // Sub-neighborhood fallback: "Lake Nona (Orlando area)" → try "lake nona"
  for (const k of Object.keys(CITY_CENTROIDS)) {
    if (key.includes(k)) return CITY_CENTROIDS[k];
  }
  return null;
}

// City mode: centroid + jitter. Jitter radius ~0.015° ≈ 1.6 km — enough to
// spread pins in same city without misleading the viewer.
function cityJitteredPos(lead) {
  const c = cityCentroid(lead.city);
  if (!c) return null;
  const dLat = seededOffset(lead.id, "lat") * 0.015;
  const dLng = seededOffset(lead.id, "lng") * 0.015;
  return { lat: c.lat + dLat, lng: c.lng + dLng };
}

// Exact mode: client-side Google Geocoder + localStorage cache.
const GEO_CACHE_KEY = "vs-geocode-cache-v1";
function loadGeoCache() {
  try { return JSON.parse(localStorage.getItem(GEO_CACHE_KEY) || "{}"); }
  catch { return {}; }
}
function saveGeoCache(cache) {
  try { localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(cache)); }
  catch { /* quota exceeded — ignore */ }
}

async function geocodeAddress(address) {
  if (!address || !window.google?.maps) return null;
  return new Promise((resolve) => {
    const g = new window.google.maps.Geocoder();
    g.geocode({ address }, (results, status) => {
      if (status === "OK" && results?.[0]?.geometry?.location) {
        const loc = results[0].geometry.location;
        resolve({ lat: loc.lat(), lng: loc.lng() });
      } else {
        resolve(null);
      }
    });
  });
}

// ── MapContent — layout-aware inner component ──────────────────────────
function MapContent({
  leads, layout, positions, onSelect, focusedLeadId,
  cities, cityTierAgg, filters, radiusCenter, radiusMiles,
  osmPolys, routeOrder,
}) {
  const map = useMap();
  const clustererRef = useRef(null);
  const markersRef = useRef([]);
  const routeMarkersRef = useRef([]);
  const routePolyRef = useRef(null);
  const circlesRef = useRef([]);
  const polygonsRef = useRef([]);
  const radiusCircleRef = useRef(null);
  const fitOnceRef = useRef(false);

  // Clear all overlays helper
  const clearOverlays = useCallback(() => {
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current.setMap?.(null);
      clustererRef.current = null;
    }
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    routeMarkersRef.current.forEach((m) => m.setMap(null));
    routeMarkersRef.current = [];
    if (routePolyRef.current) {
      routePolyRef.current.setMap(null);
      routePolyRef.current = null;
    }
    circlesRef.current.forEach((c) => c.setMap(null));
    circlesRef.current = [];
    polygonsRef.current.forEach((p) => p.setMap(null));
    polygonsRef.current = [];
  }, []);

  useEffect(() => () => {
    clearOverlays();
    if (radiusCircleRef.current) {
      radiusCircleRef.current.setMap(null);
      radiusCircleRef.current = null;
    }
  }, [clearOverlays]);

  // Rebuild overlays on layout / positions / filters change
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    clearOverlays();
    try {
      const g = window.google.maps;

      // Apply in-map filters + geo radius before rendering.
      const validLeads = leads.filter((L) => {
        const pos = positions[L.id];
        if (!pos) return false;
        if (!matchesFilters(L, filters)) return false;
        if (radiusCenter && radiusMiles > 0
            && distMi(pos, radiusCenter) > radiusMiles) return false;
        return true;
      });

    // ── clusters / split / satellite → individual markers ──────────
    if (layout === "clusters" || layout === "split" || layout === "satellite") {
      const markers = validLeads.map((L) => {
        const pos = positions[L.id];
        const color = TIER_COLOR(L.score);
        const isFocused = focusedLeadId === L.id;
        const m = new g.Marker({
          position: pos,
          title: L.property_name || L.name || "Property",
          icon: {
            path: g.SymbolPath.CIRCLE,
            scale: isFocused ? 12 : 8,
            fillColor: color,
            fillOpacity: 0.92,
            strokeColor: isFocused ? "#0B1120" : "#FFFFFF",
            strokeWeight: isFocused ? 3 : 2,
          },
          zIndex: isFocused ? 999 : 1,
        });
        m.addListener("click", () => onSelect?.(L));
        return m;
      });
      markersRef.current = markers;
      clustererRef.current = new MarkerClusterer({ map, markers });
    }

    // ── heatmap → stacked circles weighted by $ opportunity midpoint ──
    if (layout === "heatmap") {
      // Compute max opportunity to normalise opacity/radius.
      const maxOpp = validLeads.reduce(
        (m, L) => Math.max(m, leadMidOpportunity(L)), 1);
      validLeads.forEach((L) => {
        const pos = positions[L.id];
        const opp = leadMidOpportunity(L);
        const ratio = maxOpp > 0 ? opp / maxOpp : 0;   // 0..1
        // Use tier color for the *hue* but let opportunity drive opacity+radius
        const col = TIER_COLOR(L.score);
        [
          { r: 4500 + 2000 * ratio,  a: 0.35 },
          { r: 8000 + 3500 * ratio,  a: 0.20 },
          { r: 14000 + 5000 * ratio, a: 0.10 },
        ].forEach(({ r, a }) => {
          const c = new g.Circle({
            strokeOpacity: 0,
            fillColor: col,
            fillOpacity: a * Math.max(0.3, ratio),
            map, center: pos, radius: r,
            clickable: false, zIndex: 1,
          });
          circlesRef.current.push(c);
        });
      });
    }

    // ── territories → GeoJSON-approx polygons per neighbourhood ────
    if (layout === "territories") {
      // Aggregate stats per city from the filtered leads (respect the
      // current tier/flock/radius filters instead of using the raw
      // /geo-map response so the choropleth reacts live).
      const agg = new Map();
      validLeads.forEach((L) => {
        const key = (L.city || "").toLowerCase().split("(")[0].trim();
        for (const p of NEIGHBORHOOD_POLYGONS) {
          const pk = p.city.toLowerCase();
          if (!key || (!key.includes(pk) && !pk.includes(key))) continue;
          if (!agg.has(p.city)) {
            agg.set(p.city, { count: 0, score_sum: 0, crit: 0, opp: 0 });
          }
          const a = agg.get(p.city);
          a.count += 1;
          a.score_sum += Number(L.score) || 0;
          if ((L.score || 0) >= 90) a.crit += 1;
          a.opp += leadMidOpportunity(L);
          break;
        }
      });
      NEIGHBORHOOD_POLYGONS.forEach((p) => {
        const a = agg.get(p.city);
        const hasData = !!a && a.count > 0;
        const avgScore = hasData ? a.score_sum / a.count : 0;
        const col = hasData ? TIER_COLOR(avgScore) : "#475569";
        // Prefer real OSM polygon when available in the cache; fallback to
        // the hardcoded rectangle. `osmPolys[name].ring === null` is a
        // negative-cache entry (OSM had no match) — also uses fallback.
        const osmEntry = osmPolys?.[p.city];
        const source = (osmEntry && osmEntry.ring && osmEntry.ring.length >= 3)
          ? osmEntry.ring : p.ring;
        const path = source.map(([lat, lng]) => ({ lat, lng }));
        const poly = new g.Polygon({
          paths: path, strokeColor: col,
          strokeOpacity: 0.9, strokeWeight: 1.5,
          fillColor: col, fillOpacity: hasData ? 0.35 : 0.08,
          map, clickable: true, zIndex: 1,
        });
        poly.addListener("click", () => {
          onSelect?.({
            _territoryClick: true,
            city: p.city,
            avg_score: Math.round(avgScore),
            count: hasData ? a.count : 0,
            critical_count: hasData ? a.crit : 0,
            pipeline_headline: hasData ? `$${a.opp.toLocaleString()}` : "—",
          });
        });
        polygonsRef.current.push(poly);
      });
    }

    // ── Route optimizer overlay (Iter311) ───────────────────────────
    if (routeOrder && routeOrder.length > 0) {
      const routePositions = routeOrder
        .map((id) => positions[id])
        .filter(Boolean);
      if (routePositions.length >= 2) {
        routePolyRef.current = new g.Polyline({
          path: routePositions, map,
          strokeColor: "#C9A24E", strokeOpacity: 0.9, strokeWeight: 4,
          geodesic: true, zIndex: 5,
        });
      }
      // Draw numbered stop markers (1..N) on top of the base cluster.
      routePositions.forEach((pos, i) => {
        const m = new g.Marker({
          position: pos, map,
          label: { text: String(i + 1), color: "#0B1120",
                    fontWeight: "800", fontSize: "12px" },
          icon: {
            path: g.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: "#F1F5F9",
            fillOpacity: 1,
            strokeColor: "#C9A24E",
            strokeWeight: 3,
          },
          zIndex: 10,
        });
        routeMarkersRef.current.push(m);
      });
    }
    } catch (err) {
      console.error("[VehicleSecurityMap] overlay render failed:", err);
    }
  }, [map, layout, positions, leads, cities, cityTierAgg, focusedLeadId,
       filters, radiusCenter, radiusMiles, osmPolys, routeOrder,
       onSelect, clearOverlays]);

  // Draw / clear the geo-radius circle centered on `radiusCenter`.
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    if (radiusCircleRef.current) {
      radiusCircleRef.current.setMap(null);
      radiusCircleRef.current = null;
    }
    if (radiusCenter && radiusMiles > 0) {
      radiusCircleRef.current = new window.google.maps.Circle({
        strokeColor: "#C9A24E", strokeOpacity: 0.9, strokeWeight: 2,
        fillColor: "#C9A24E", fillOpacity: 0.06,
        center: radiusCenter, radius: radiusMiles * MI_TO_METERS,
        map, clickable: false, zIndex: 0,
      });
      map.panTo(radiusCenter);
      // Fit to circle
      const bounds = radiusCircleRef.current.getBounds();
      if (bounds) map.fitBounds(bounds, 40);
    }
  }, [map, radiusCenter, radiusMiles]);

  // Fit bounds once on first non-empty render (unless a radius is set — then
  // the radius circle drives the framing above).
  useEffect(() => {
    if (!map || !window.google?.maps || fitOnceRef.current) return;
    if (radiusCenter && radiusMiles > 0) return;
    const positionsArr = Object.values(positions || {});
    const territoriesArr = (cities || []).filter((c) => c.lat && c.lng);
    const total = positionsArr.length + territoriesArr.length;
    if (total === 0) return;
    const b = new window.google.maps.LatLngBounds();
    positionsArr.forEach((p) => b.extend(p));
    territoriesArr.forEach((c) => b.extend({ lat: c.lat, lng: c.lng }));
    map.fitBounds(b, 60);
    fitOnceRef.current = true;
  }, [map, positions, cities, radiusCenter, radiusMiles]);

  // Recenter on focused lead (split view sync)
  useEffect(() => {
    if (!map || !focusedLeadId || !positions[focusedLeadId]) return;
    map.panTo(positions[focusedLeadId]);
    const currentZoom = map.getZoom() || 8;
    if (currentZoom < 12) map.setZoom(12);
  }, [map, focusedLeadId, positions]);

  return null;
}

// ── SegmentedLayoutControl ────────────────────────────────────────────
function LayoutControl({ layout, onChange, isMobile }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const active = LAYOUTS.find((l) => l.key === layout) || LAYOUTS[0];

  if (isMobile) {
    return (
      <div className="relative" data-testid="vsm-layout-dropdown">
        <button type="button" onClick={() => setDropdownOpen((v) => !v)}
          className="h-8 px-3 rounded-lg border border-white/15 bg-black/40 flex items-center gap-1.5 text-[11.5px] font-semibold text-white">
          <active.icon className="w-3.5 h-3.5" /> {active.label}
          <ChevronDown className="w-3 h-3 ml-1" />
        </button>
        {dropdownOpen && (
          <div className="absolute z-20 top-full mt-1 left-0 rounded-lg border border-white/15 bg-[#0F172A] shadow-2xl overflow-hidden">
            {LAYOUTS.map((L) => (
              <button key={L.key} type="button"
                onClick={() => { onChange(L.key); setDropdownOpen(false); }}
                data-testid={`vsm-layout-option-${L.key}`}
                className={`w-full h-8 px-3 flex items-center gap-2 text-[11.5px] hover:bg-white/10 ${
                  L.key === layout ? "text-white bg-white/5" : "text-slate-300"}`}>
                <L.icon className="w-3.5 h-3.5" /> {L.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="inline-flex rounded-lg border border-white/15 bg-black/30 p-0.5"
         data-testid="vsm-layout-segmented">
      {LAYOUTS.map((L) => {
        const active = L.key === layout;
        return (
          <button key={L.key} type="button" onClick={() => onChange(L.key)}
            data-testid={`vsm-layout-btn-${L.key}`}
            className={`h-7 px-2.5 rounded-md flex items-center gap-1.5 text-[11px] font-semibold transition-colors ${
              active
                ? "bg-white/10 text-white"
                : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
            <L.icon className="w-3.5 h-3.5" /> {L.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Info Card overlay ─────────────────────────────────────────────────
function InfoCard({ item, onClose, onOpenStreetView, onLeadAction, layout }) {
  const { t } = useLang();
  if (!item) return null;
  const isTerritory = item._territoryClick;
  return (
    <div
      className="absolute bottom-3 left-3 z-30 w-[280px] max-w-[calc(100%-24px)] rounded-xl border border-white/15 bg-[#0F172A]/95 backdrop-blur p-3 shadow-2xl"
      data-testid="vsm-info-card">
      <div className="flex items-start gap-2 mb-2">
        <div className="flex-1 min-w-0">
          {isTerritory ? (
            <>
              <div className="text-[12px] font-extrabold text-white truncate">
                {item.city}, FL
              </div>
              <div className="text-[10px] text-slate-400">
                {item.count} leads · avg {item.avg_score}
              </div>
            </>
          ) : (
            <>
              <div className="text-[12px] font-extrabold text-white truncate"
                   title={item.property_name}>
                {item.property_name || item.name || "Propriedade"}
              </div>
              <div className="text-[10px] text-slate-400 truncate">
                {item.segment || item.segment_label || ""}
                {item.city ? ` · ${item.city}` : ""}
              </div>
            </>
          )}
        </div>
        <button type="button" onClick={onClose}
          data-testid="vsm-info-close"
          className="w-6 h-6 rounded border border-white/15 flex items-center justify-center text-slate-400 hover:text-white shrink-0">
          <X className="w-3 h-3" />
        </button>
      </div>

      {isTerritory ? (
        <div className="space-y-1 text-[11px]">
          {item.critical_count > 0 && (
            <div className="text-red-300">🔥 {item.critical_count} critical (90+)</div>
          )}
          <div className="text-slate-300 tabular-nums">Pipeline mid: {item.pipeline_headline}</div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1.5 flex-wrap mb-2">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-bold"
                  style={{
                    background: TIER_COLOR(item.score) + "26",
                    color: TIER_COLOR(item.score),
                    border: `1px solid ${TIER_COLOR(item.score)}55`,
                  }}>
              {TIER_LABEL(item.score)} · {item.score || 0}
            </span>
            {item.flock_detected && (
              <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
                {t("vsm.flock")}
              </span>
            )}
          </div>
          {Array.isArray(item.signals) && item.signals.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {item.signals.slice(0, 4).map((s) => (
                <span key={s} className="text-[9.5px] px-1.5 py-0.5 rounded bg-white/5 text-slate-300 border border-white/10 capitalize">
                  {s}
                </span>
              ))}
              {item.signals.length > 4 && (
                <span className="text-[9.5px] text-slate-500">+{item.signals.length - 4}</span>
              )}
            </div>
          )}
          {item.estimated_contract && (
            <div className="text-[10.5px] text-slate-400 mb-2">
              {t("vsm.info.est_contract")} <span className="text-emerald-300 tabular-nums">
                ${(item.estimated_contract.tech_low || 0).toLocaleString()}
                {" – "}
                ${(item.estimated_contract.tech_high || 0).toLocaleString()}
              </span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <button type="button"
              onClick={() => onLeadAction?.(item)}
              data-testid="vsm-info-action"
              className="flex-1 h-7 rounded bg-white/10 hover:bg-white/15 text-[11px] font-semibold text-white">
              {t("vsm.info.details")}
            </button>
            {layout === "satellite" && (
              <button type="button"
                onClick={() => onOpenStreetView?.(item)}
                data-testid="vsm-info-streetview"
                title={t("vsm.info.street_view_open")}
                className="h-7 px-2 rounded bg-white/5 hover:bg-white/15 border border-white/10 text-[11px] text-slate-300 inline-flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> {t("vsm.info.street_view")}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Filters & Radius toolbar (Iter310) ────────────────────────────────
const TIER_KEYS = [
  { key: "all",       tKey: "vsm.tier.all",       color: "#94A3B8" },
  { key: "critical",  tKey: "vsm.tier.critical",  color: "#EF4444", emoji: "🔥" },
  { key: "very_high", tKey: "vsm.tier.very_high", color: "#F97316", emoji: "🟠" },
  { key: "high",      tKey: "vsm.tier.high",      color: "#EAB308", emoji: "🟡" },
  { key: "medium",    tKey: "vsm.tier.medium",    color: "#0EA5E9", emoji: "🔵" },
  { key: "low",       tKey: "vsm.tier.low",       color: "#94A3B8", emoji: "⚪" },
];

function InMapFiltersBar({ filters, onFiltersChange, count, total }) {
  const { t } = useLang();
  const setTier = (t) => onFiltersChange?.({ ...filters, tier: t === "all" ? "" : t });
  const setFlock = (f) => onFiltersChange?.({ ...filters, flock: f });
  const activeTier = (filters?.tier || "all");
  const activeFlock = (filters?.flock || "all");
  return (
    <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2 flex-wrap"
         data-testid="vsm-filters-bar">
      <span className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-widest font-bold text-slate-500 shrink-0">
        <Filter className="w-3 h-3" /> {t("vsm.tier")}
      </span>
      {TIER_KEYS.map((tk) => {
        const active = tk.key === activeTier || (tk.key === "all" && !filters?.tier);
        return (
          <button key={tk.key} type="button" onClick={() => setTier(tk.key)}
            data-testid={`vsm-tier-chip-${tk.key}`}
            className={`h-6 px-2 rounded-full border text-[10.5px] font-semibold inline-flex items-center gap-1 transition-colors ${
              active
                ? "bg-white/15 text-white"
                : "bg-black/30 text-slate-400 hover:text-white"}`}
            style={active ? { borderColor: tk.color + "aa" } : { borderColor: "rgba(255,255,255,0.15)" }}>
            {tk.emoji && <span>{tk.emoji}</span>}
            {t(tk.tKey)}
          </button>
        );
      })}
      <div className="w-px h-4 bg-white/10 mx-1" />
      <span className="text-[9.5px] uppercase tracking-widest font-bold text-slate-500 shrink-0">{t("vsm.flock_alpr")}</span>
      {[
        { k: "all",     tKey: "vsm.flock.all"     },
        { k: "only",    tKey: "vsm.flock.only"    },
        { k: "exclude", tKey: "vsm.flock.exclude" },
      ].map((f) => {
        const active = f.k === activeFlock;
        return (
          <button key={f.k} type="button" onClick={() => setFlock(f.k)}
            data-testid={`vsm-flock-chip-${f.k}`}
            className={`h-6 px-2 rounded-full border text-[10.5px] font-semibold ${
              active
                ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-200"
                : "border-white/15 bg-black/30 text-slate-400 hover:text-white"}`}>
            {t(f.tKey)}
          </button>
        );
      })}
      <div className="ml-auto text-[10px] text-slate-400 tabular-nums shrink-0"
           data-testid="vsm-filters-count">
        {t("vsm.leads_count", { count, total })}
      </div>
    </div>
  );
}

function RadiusBar({ radiusMiles, onRadiusChange, address, onAddressChange, onSearch, geocoding, radiusActive }) {
  const { t } = useLang();
  const [localAddr, setLocalAddr] = useState(address || "");
  useEffect(() => setLocalAddr(address || ""), [address]);
  return (
    <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2 flex-wrap"
         data-testid="vsm-radius-bar">
      <div className="flex items-center gap-1.5 flex-1 min-w-[220px]">
        <Search className="w-3.5 h-3.5 text-slate-500 shrink-0" />
        <input
          type="text"
          value={localAddr}
          onChange={(e) => setLocalAddr(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onSearch?.(localAddr); } }}
          placeholder={t("vsm.address_placeholder")}
          data-testid="vsm-address-input"
          className="flex-1 h-7 px-2 rounded-md bg-black/40 border border-white/15 text-[11.5px] text-white placeholder:text-slate-500 focus:outline-none focus:border-white/30" />
        <button type="button"
          onClick={() => onSearch?.(localAddr)}
          data-testid="vsm-address-search"
          className="h-7 px-2 rounded-md bg-white/10 hover:bg-white/15 border border-white/15 text-[11px] font-semibold text-white inline-flex items-center gap-1">
          {geocoding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Crosshair className="w-3 h-3" />}
          {t("btn.go") /* fallback if missing key falls back to "btn.go" */}
        </button>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <span className="text-[9.5px] uppercase tracking-widest font-bold text-slate-500">{t("vsm.radius")}</span>
        {RADIUS_OPTIONS_MI.map((mi) => (
          <button key={mi} type="button"
            onClick={() => onRadiusChange?.(mi)}
            data-testid={`vsm-radius-${mi}`}
            className={`h-6 px-2 rounded-md text-[10.5px] font-semibold border ${
              radiusMiles === mi
                ? "bg-[#C9A24E]/15 border-[#C9A24E]/60 text-[#C9A24E]"
                : "bg-black/30 border-white/15 text-slate-400 hover:text-white"}`}>
            {mi === 0 ? t("vsm.radius.off") : `${mi}mi`}
          </button>
        ))}
        {radiusActive && (
          <button type="button"
            onClick={() => { onRadiusChange?.(0); onAddressChange?.(""); }}
            data-testid="vsm-radius-clear"
            title={t("vsm.radius.clear")}
            className="h-6 w-6 rounded-md bg-black/30 border border-white/15 flex items-center justify-center text-slate-400 hover:text-white">
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Route Optimizer Bar (Iter311) ─────────────────────────────────────
function RouteBar({ visibleCount, isRouting, hasRoute, onOptimize, onClear, gmapsUrl }) {
  const { t } = useLang();
  return (
    <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2 flex-wrap"
         data-testid="vsm-route-bar">
      <span className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-widest font-bold text-slate-500 shrink-0">
        {t("vsm.route.optimizer")}
      </span>
      <button type="button"
        onClick={onOptimize}
        disabled={visibleCount < 2 || isRouting}
        data-testid="vsm-route-optimize"
        className={`h-6 px-2.5 rounded-md text-[10.5px] font-semibold border inline-flex items-center gap-1 ${
          visibleCount < 2
            ? "border-white/10 bg-black/20 text-slate-600 cursor-not-allowed"
            : "border-[#C9A24E]/60 bg-[#C9A24E]/15 text-[#C9A24E] hover:bg-[#C9A24E]/25"}`}>
        <MapPin className="w-3 h-3" />
        {t("vsm.route.optimize", { n: visibleCount })}
      </button>
      {hasRoute && (
        <>
          <a href={gmapsUrl} target="_blank" rel="noopener noreferrer"
             data-testid="vsm-route-navigate"
             className="h-6 px-2.5 rounded-md text-[10.5px] font-semibold border border-sky-500/60 bg-sky-500/15 text-sky-200 hover:bg-sky-500/25 inline-flex items-center gap-1">
            <ExternalLink className="w-3 h-3" />
            {t("vsm.route.navigate")}
          </a>
          <button type="button"
            onClick={onClear}
            data-testid="vsm-route-clear"
            className="h-6 w-6 rounded-md bg-black/30 border border-white/15 flex items-center justify-center text-slate-400 hover:text-white">
            <X className="w-3 h-3" />
          </button>
        </>
      )}
      <span className="ml-auto text-[10px] text-slate-500 italic shrink-0">
        {t("vsm.route.hint")}
      </span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────
export default function VehicleSecurityMap({
  leads = [],
  cities = [],                    // from /vehicle-security/geo-map
  layout = "clusters",
  onLayoutChange,
  privacyMode = "city",           // "city" | "exact"
  onPrivacyChange,
  isMaster = false,
  onLeadSelect,
  focusedLeadId = null,
  filters,                        // { tier: '', flock: 'all' }
  onFiltersChange,
  radiusCenter = null,            // lifted state — page owns it now
  onRadiusCenterChange,
  radiusMiles = 0,
  onRadiusMilesChange,
  routeOrder = [],                // ordered array of lead ids (Iter311)
  onRouteChange,
  height = 420,
}) {
  const { t } = useLang();
  const [selectedItem, setSelectedItem] = useState(null);
  const [geoCache, setGeoCache] = useState(loadGeoCache());
  const [geocoding, setGeocoding] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false);

  // Iter310 · Address query + geocode-in-flight indicator (local only)
  const [addressQuery, setAddressQuery] = useState("");
  const [addressGeocoding, setAddressGeocoding] = useState(false);

  // Iter311 · Real OSM polygons cached in localStorage
  const [osmPolys, setOsmPolys] = useState(loadOsmCache());
  const [osmLoading, setOsmLoading] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Build positions map.
  //   city mode → centroid + jitter (sync)
  //   exact mode → geocode uncached addresses in the background
  const positions = useMemo(() => {
    const out = {};
    for (const L of leads) {
      if (privacyMode === "exact") {
        const addr = [L.property_address, L.city, L.state]
          .filter(Boolean).join(", ");
        const cached = geoCache[addr];
        if (cached) out[L.id] = cached;
        else {
          // fallback to city centroid while geocoding
          const p = cityJitteredPos(L);
          if (p) out[L.id] = p;
        }
      } else {
        const p = cityJitteredPos(L);
        if (p) out[L.id] = p;
      }
    }
    return out;
  }, [leads, privacyMode, geoCache]);

  // Background geocode addresses missing from cache when in exact mode.
  useEffect(() => {
    if (privacyMode !== "exact" || !window.google?.maps) return;
    const missing = leads
      .map((L) => ({ id: L.id, addr: [L.property_address, L.city, L.state].filter(Boolean).join(", ") }))
      .filter((x) => x.addr && !geoCache[x.addr]);
    if (missing.length === 0) return;
    setGeocoding(true);
    let cancelled = false;
    (async () => {
      const next = { ...geoCache };
      for (const m of missing.slice(0, 20)) {   // cap to 20 per pass
        const coords = await geocodeAddress(m.addr);
        if (coords) next[m.addr] = coords;
        await new Promise((r) => setTimeout(r, 80)); // gentle pace
        if (cancelled) return;
      }
      saveGeoCache(next);
      if (!cancelled) {
        setGeoCache(next);
        setGeocoding(false);
      }
    })();
    return () => { cancelled = true; };
  }, [privacyMode, leads, geoCache]);

  // Sync when parent hovers a table row.
  useEffect(() => {
    if (!focusedLeadId) return;
    const L = leads.find((x) => x.id === focusedLeadId);
    if (L) setSelectedItem(L);
  }, [focusedLeadId, leads]);

  const handleStreetView = useCallback((lead) => {
    const p = positions[lead.id];
    if (!p) return;
    // Native Google Maps Street View URL for a new tab — quicker than in-app pano.
    const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${p.lat},${p.lng}`;
    window.open(url, "_blank", "noopener");
  }, [positions]);

  // Iter310 · Address search → geocode → center + apply default radius.
  const handleAddressSearch = useCallback(async (addr) => {
    const q = (addr || "").trim();
    if (!q) return;
    if (!window.google?.maps) return;
    setAddressGeocoding(true);
    try {
      const coords = await geocodeAddress(q);
      if (coords) {
        onRadiusCenterChange?.(coords);
        setAddressQuery(q);
        if (!radiusMiles) onRadiusMilesChange?.(10);  // sensible default
      }
    } finally {
      setAddressGeocoding(false);
    }
  }, [radiusMiles, onRadiusCenterChange, onRadiusMilesChange]);

  const handleRadiusChange = useCallback((mi) => {
    onRadiusMilesChange?.(mi);
    if (mi === 0) onRadiusCenterChange?.(null);
  }, [onRadiusCenterChange, onRadiusMilesChange]);

  // Iter311 · When territories tab is active, fetch missing OSM polygons
  // for the 16 canonical neighborhoods. Cached 7 days in localStorage.
  useEffect(() => {
    if (layout !== "territories") return;
    const now = Date.now();
    const missing = NEIGHBORHOOD_POLYGONS
      .map((p) => p.city)
      .filter((name) => {
        const entry = osmPolys[name];
        return !entry || (now - (entry.ts || 0)) > OSM_TTL_MS;
      });
    if (missing.length === 0) return;
    let cancelled = false;
    setOsmLoading(true);
    (async () => {
      const next = { ...osmPolys };
      for (const name of missing) {
        const res = await fetchOsmPolygon(name);
        // Cache even negative results (so we don't retry every second)
        next[name] = { ts: Date.now(), ring: res?.ring || null };
        await new Promise((r) => setTimeout(r, 500)); // gentle pacing
        if (cancelled) return;
      }
      saveOsmCache(next);
      if (!cancelled) { setOsmPolys(next); setOsmLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);

  // Live-filtered lead count for the badge in the filter bar.
  const filteredLeads = useMemo(() => leads.filter((L) => {
    const pos = positions[L.id];
    if (!pos) return false;
    if (!matchesFilters(L, filters)) return false;
    if (radiusCenter && radiusMiles > 0
        && distMi(pos, radiusCenter) > radiusMiles) return false;
    return true;
  }), [leads, positions, filters, radiusCenter, radiusMiles]);
  const filteredCount = filteredLeads.length;

  // Iter311 · Route optimizer state — visit order is a plain list of ids.
  const [isRouting, setIsRouting] = useState(false);
  const handleOptimizeRoute = useCallback(() => {
    if (filteredLeads.length < 2) return;
    setIsRouting(true);
    try {
      // Origin: radiusCenter when active, else the first lead.
      const stops = filteredLeads
        .map((L) => ({ id: L.id, ...(positions[L.id] || {}) }))
        .filter((s) => s.lat != null);
      const start = radiusCenter || stops[0];
      const ordered = optimizeVisitOrder(start, [...stops]);
      onRouteChange?.(ordered.map((s) => s.id));
    } finally {
      setIsRouting(false);
    }
  }, [filteredLeads, positions, radiusCenter, onRouteChange]);

  const handleClearRoute = useCallback(() => onRouteChange?.([]), [onRouteChange]);

  const gmapsRouteUrl = useMemo(() => {
    if (!routeOrder || routeOrder.length < 1) return "";
    const stops = routeOrder
      .map((id) => positions[id])
      .filter((p) => p && p.lat != null);
    if (stops.length === 0) return "";
    return buildGmapsDirectionsUrl(radiusCenter, stops);
  }, [routeOrder, positions, radiusCenter]);

  if (!GMAPS_KEY) {
    return (
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-[12px] text-amber-200"
           data-testid="vsm-missing-key">
        {t("vsm.missing_key")}
      </div>
    );
  }

  const mapTypeId = layout === "satellite" ? "hybrid" : "roadmap";
  const showControls = true;

  return (
    <div className="rounded-xl border border-white/10 bg-black/20 overflow-hidden"
         data-testid="vsm-root">
      {/* Header: layout segmented + privacy toggle + legend */}
      {showControls && (
        <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2 flex-wrap">
          <LayoutControl layout={layout} onChange={onLayoutChange} isMobile={isMobile} />

          <div className="ml-auto flex items-center gap-2">
            {geocoding && (
              <span className="inline-flex items-center gap-1 text-[10px] text-sky-300"
                    data-testid="vsm-geocoding-badge">
                <Loader2 className="w-3 h-3 animate-spin" /> {t("vsm.geocoding")}
              </span>
            )}
            {/* Privacy toggle — MASTER only can switch to exact */}
            <button type="button"
              onClick={() => {
                if (!isMaster) return;
                onPrivacyChange?.(privacyMode === "exact" ? "city" : "exact");
              }}
              disabled={!isMaster}
              title={isMaster
                ? (privacyMode === "exact" ? t("vsm.privacy.exact_title") : t("vsm.privacy.city_title"))
                : t("vsm.privacy.master_required")}
              data-testid="vsm-privacy-toggle"
              className={`h-7 px-2.5 rounded-lg border text-[10.5px] font-semibold inline-flex items-center gap-1.5 ${
                privacyMode === "exact"
                  ? "border-red-500/50 bg-red-500/10 text-red-200"
                  : "border-white/15 bg-black/40 text-slate-300 hover:text-white"} ${
                !isMaster ? "opacity-50 cursor-not-allowed" : ""}`}>
              {privacyMode === "exact"
                ? <><Eye className="w-3 h-3" /> {t("vsm.privacy.exact")}</>
                : <><EyeOff className="w-3 h-3" /> {t("vsm.privacy.city")}</>}
            </button>
          </div>
        </div>
      )}

      {/* Iter310 · In-map filters (tier + flock) — real-time client-side */}
      <InMapFiltersBar
        filters={filters}
        onFiltersChange={onFiltersChange}
        count={filteredCount}
        total={leads.length} />

      {/* Iter310 · Address search + radius (miles) */}
      <RadiusBar
        radiusMiles={radiusMiles}
        onRadiusChange={handleRadiusChange}
        address={addressQuery}
        onAddressChange={setAddressQuery}
        onSearch={handleAddressSearch}
        geocoding={addressGeocoding}
        radiusActive={!!radiusCenter && radiusMiles > 0} />

      {/* Iter311 · Route optimizer */}
      <RouteBar
        visibleCount={filteredCount}
        isRouting={isRouting}
        hasRoute={routeOrder && routeOrder.length > 0}
        onOptimize={handleOptimizeRoute}
        onClear={handleClearRoute}
        gmapsUrl={gmapsRouteUrl} />

      {osmLoading && layout === "territories" && (
        <div className="px-3 py-1 text-[10px] text-sky-300 inline-flex items-center gap-1 border-b border-white/10"
             data-testid="vsm-osm-loading">
          <Loader2 className="w-3 h-3 animate-spin" />
          {t("vsm.osm_loading")}
        </div>
      )}

      {/* Map + info card overlay */}
      <div className="relative" style={{ height }}>
        <APIProvider apiKey={GMAPS_KEY}>
          <GMap
            style={{ width: "100%", height: "100%" }}
            defaultCenter={{ lat: 28.5384, lng: -81.3789 }}
            defaultZoom={9}
            mapTypeId={mapTypeId}
            gestureHandling="greedy"
            streetViewControl={layout === "satellite"}
            mapTypeControl={false}
            fullscreenControl={false}>
            <MapContent
              leads={leads}
              layout={layout}
              positions={positions}
              cities={cities}
              filters={filters}
              radiusCenter={radiusCenter}
              radiusMiles={radiusMiles}
              osmPolys={osmPolys}
              routeOrder={routeOrder}
              onSelect={(item) => {
                setSelectedItem(item);
                if (item && !item._territoryClick) onLeadSelect?.(item);
              }}
              focusedLeadId={focusedLeadId} />
          </GMap>
        </APIProvider>

        <InfoCard
          item={selectedItem}
          layout={layout}
          onClose={() => setSelectedItem(null)}
          onOpenStreetView={handleStreetView}
          onLeadAction={(L) => onLeadSelect?.(L)} />

        {/* Empty state */}
        {leads.length === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-[11.5px] text-slate-500 pointer-events-none"
               data-testid="vsm-empty">
            <MapPin className="w-4 h-4" />
            {t("vsm.empty")}
          </div>
        )}
      </div>

      {/* Legend footer */}
      <div className="px-3 py-1.5 border-t border-white/10 flex items-center gap-3 flex-wrap text-[9.5px] text-slate-400">
        <span className="font-bold uppercase tracking-widest">{t("vsm.legend")}</span>
        {[
          ["Critical 90+", "#EF4444"],
          ["Very High 80-89", "#F97316"],
          ["High 70-79", "#EAB308"],
          ["Medium 60-69", "#0EA5E9"],
          ["Low <60", "#94A3B8"],
        ].map(([label, col]) => (
          <span key={label} className="inline-flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: col }} />
            {label}
          </span>
        ))}
        {privacyMode === "city" && (
          <span className="ml-auto inline-flex items-center gap-1 text-slate-500 italic">
            <Info className="w-3 h-3" />
            {t("vsm.footer.city_mode")}
          </span>
        )}
      </div>
    </div>
  );
}
